import './polotno/index.scss';

import React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';

import { PhotoWellContextProvider } from './context/photoWellContext';
import { PhotoCropperContextProvider } from './context/photoCropperContext';
import { DragDropContextProvider } from './context/dragDropContext';
import { ResetStyle, GeneralStyle } from './components/theme';
import { EditorItemContextProvider } from './context/editorItemContext';
import { TextEditorContextProvider } from './context/textEditorContext';
import { AncestryFamilyTreeContextProvider } from './context/ancestryFamilyTreeContext';
import { PageSelectorContextProvider } from './context/pageSelectorContext';
import { UploaderContextProvider } from './context/uploaderContext';
import { ContentWizardContextProvider } from './contentWizard/contentWizardContext';

import DefaultEditor from './default';
import BookEditor from './book';
import CardEditor from './card';
import CouponEditor from './coupon';
import InstagramEditor from './instagram';
import NewEditor from './new';
import TreeEditor from './tree';

import SETTINGS from './settings';

import UnsupportedDevice, { useDeviceSupport } from './components/unsupportedDevice';

const SkuEditor = props => {
  const [isDeviceSupported, currentDevice] = useDeviceSupport(props.config.devices);

  const { Component, theme, ...otherProps } = props;

  return (
    <ThemeProvider theme={theme}>
      <ResetStyle />
      <GeneralStyle />
      <EditorItemContextProvider id={props.id} {...otherProps}>
        <UploaderContextProvider>
          <PageSelectorContextProvider>
            <PhotoWellContextProvider id={props.id} item={props.item}>
              <PhotoCropperContextProvider>
                <DragDropContextProvider id={props.id}>
                  <TextEditorContextProvider>
                    <AncestryFamilyTreeContextProvider>
                      <ContentWizardContextProvider>
                        {isDeviceSupported ? (
                          <Component {...otherProps} />
                        ) : (
                          <UnsupportedDevice
                            device={currentDevice}
                            supportedDevices={props.config.devices}
                          />
                        )}
                      </ContentWizardContextProvider>
                    </AncestryFamilyTreeContextProvider>
                  </TextEditorContextProvider>
                </DragDropContextProvider>
              </PhotoCropperContextProvider>
            </PhotoWellContextProvider>
          </PageSelectorContextProvider>
        </UploaderContextProvider>
      </EditorItemContextProvider>
    </ThemeProvider>
  );
};

SkuEditor.propTypes = {
  id: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  Component: PropTypes.func.isRequired,
};

const CustomEditor = props => {
  const [isDeviceSupported, currentDevice] = useDeviceSupport(props.config.devices);

  const { Component, theme, ...otherProps } = props;

  return (
    <ThemeProvider theme={theme}>
      <ResetStyle />
      <GeneralStyle />

      <UploaderContextProvider>
        {isDeviceSupported ? (
          <Component {...otherProps} />
        ) : (
          <UnsupportedDevice device={currentDevice} supportedDevices={props.config.devices} />
        )}
      </UploaderContextProvider>
    </ThemeProvider>
  );
};

CustomEditor.propTypes = {
  id: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  Component: PropTypes.func.isRequired,
};

export const editorComponent = (type, props) => {
  let Component;

  switch (type) {
    case 'book':
      Component = BookEditor;
      break;

    case 'card':
      Component = CardEditor;
      break;

    case 'coupon':
      Component = CouponEditor;
      break;

    case 'instagram':
      Component = InstagramEditor;
      break;

    case 'default':
      Component = DefaultEditor;
      break;

    case 'tree':
      Component = TreeEditor;
      break;

    default:
      Component = NewEditor;
  }

  if (props.product.group === 'Photo Cards') {
    Component = CardEditor;
  }

  if (!props.template) {
    return <CustomEditor key={props.id} Component={Component} {...props} />;
  }

  return <SkuEditor key={props.id} Component={Component} {...props} />;
};

const init = config => {
  Object.keys(SETTINGS).forEach(key => {
    if (config[key]) {
      SETTINGS[key] = config[key];
    }
  });
};

export default init;
